/**
 * @owners/format — the module's number rules, in one place.
 *
 * Product rule (README "Ground rules", DECISIONS "Data presentation rules"):
 * actual numbers first, then " · ", then the percent. No brackets.
 *   "2,184 · 65.0%"      "−$16,500 · −6.3%"      "+2.6 pts"
 *
 * Every page and every generated report formats through these helpers so the
 * rule can't drift page by page. Negative numbers use the true minus sign
 * (U+2212), never a hyphen and never parentheses.
 */

export const MINUS = "−";
export const SEP = " · ";

export interface NumOpts {
  /** Decimal places (default 0). */
  decimals?: number;
  /** Prefix "+" on positive values (for deltas). */
  signed?: boolean;
}

function groupAbs(n: number, decimals: number): string {
  return Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function sign(n: number, signed: boolean | undefined, decimals: number): string {
  // A value that rounds to zero is shown unsigned ("0", "$0") — "−0" reads as an error.
  const rounded = Number(n.toFixed(decimals));
  if (rounded < 0) return MINUS;
  if (signed && rounded > 0) return "+";
  return "";
}

/** 2184 → "2,184"; −16.5 with decimals 1 → "−16.5". */
export function num(n: number | null | undefined, opts: NumOpts = {}): string {
  if (n == null || !Number.isFinite(n)) return "—";
  const d = opts.decimals ?? 0;
  return sign(n, opts.signed, d) + groupAbs(n, d);
}

/** −16500 → "−$16,500"; signed: 16500 → "+$16,500". */
export function money(n: number | null | undefined, opts: NumOpts = {}): string {
  if (n == null || !Number.isFinite(n)) return "—";
  const d = opts.decimals ?? 0;
  return sign(n, opts.signed, d) + "$" + groupAbs(n, d);
}

/**
 * Compact money for tiles: 24_800_000 → "$24.8M", 2_080_000 → "$2.08M",
 * 189_100 → "$189.1K". Two decimals below 10 units, one above. Pass `unit`
 * to keep a row of tiles on the same unit (970_000 with unit "M" → "$0.97M").
 * Below $10,000 (and no unit forced) falls back to full money().
 */
export function moneyCompact(
  n: number | null | undefined,
  opts: { signed?: boolean; unit?: "K" | "M" | "B" } = {},
): string {
  if (n == null || !Number.isFinite(n)) return "—";
  const a = Math.abs(n);
  if (!opts.unit && a < 10_000) return money(n, { signed: opts.signed });
  const unit = opts.unit ?? (a >= 1e9 ? "B" : a >= 1e6 ? "M" : "K");
  const scaled = n / { K: 1e3, M: 1e6, B: 1e9 }[unit];
  const d = Math.abs(scaled) < 10 ? 2 : 1;
  return sign(scaled, opts.signed, d) + "$" + groupAbs(scaled, d) + unit;
}

/** Ratio or percent value → "65.0%". Pass `ratio: true` when n is 0.65. */
export function pct(n: number | null | undefined, opts: NumOpts & { ratio?: boolean } = {}): string {
  if (n == null || !Number.isFinite(n)) return "—";
  const v = opts.ratio ? n * 100 : n;
  const d = opts.decimals ?? 1;
  return sign(v, opts.signed, d) + groupAbs(v, d) + "%";
}

/** Percentage-point change → "+2.6 pts". */
export function pts(n: number | null | undefined, decimals = 1): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return sign(n, true, decimals) + groupAbs(n, decimals) + " pts";
}

/** Multiple such as DSCR → "0.91x". */
export function times(n: number | null | undefined, decimals = 2): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return sign(n, false, decimals) + groupAbs(n, decimals) + "x";
}

/** Join a value and its percent with the module separator: "2,184 · 65.0%". */
export function withPct(value: string, percent: string): string {
  return value + SEP + percent;
}

/**
 * The most common pair: a count/amount and its share or change.
 *   valuePct(2184, 0.65, {ratio: true})              → "2,184 · 65.0%"
 *   valuePct(-16500, -6.3, {money: true, signed: true}) → "−$16,500 · −6.3%"
 */
export function valuePct(
  value: number | null | undefined,
  percent: number | null | undefined,
  opts: { money?: boolean; ratio?: boolean; signed?: boolean; decimals?: number; pctDecimals?: number } = {},
): string {
  const v = opts.money
    ? money(value, { signed: opts.signed, decimals: opts.decimals })
    : num(value, { signed: opts.signed, decimals: opts.decimals });
  const p = pct(percent, { ratio: opts.ratio, signed: opts.signed, decimals: opts.pctDecimals });
  return withPct(v, p);
}

/** Change vs a baseline: (actual, base) → "−$16,500 · −6.3%". Percent is of |base|. */
export function delta(actual: number, base: number, opts: { money?: boolean; decimals?: number } = {}): string {
  const diff = actual - base;
  const p = base === 0 ? null : (diff / Math.abs(base)) * 100;
  return valuePct(diff, p, { money: opts.money, signed: true, decimals: opts.decimals });
}
